/* React */
import React, { useMemo } from 'react';

/* Translation */
import { useTranslation } from 'react-i18next';

/* Styled Components */
import {
  DirectorContainer,
  Name,
  AssignedCenters,
  Role,
  Email,
  StyledDropdown,
  DropdownContainer,
  Overlay,
  Dots,
  CoverLetter,
} from './styles/DirectorItem';

/* Components */
import { Dropdown, Checkbox } from 'lego-dist';
import RenderIf from 'components/RenderIf';

/* Contexts, Hooks, Services */
import useCurrentUser from 'hooks/useCurrentUser';

/* Helpers */
import { ActionPermission } from 'lib/specialPermissions';

/* Images, Icons */
import { useRouter } from 'routes';
import Tooltip from '../Staff/Manage/Tooltip';
import RendererIf from 'components/RendererIf';

interface DirectorItemProps {
  director: any;
  selected?: boolean;
  toggleSelectDirector?(director: any): void;
  onClickDeleteDropDown?(id: number): void;
}

const DirectorItem: React.FC<DirectorItemProps> = ({
  director,
  onClickDeleteDropDown,
  selected,
  toggleSelectDirector
}) => {
  const {
    id,
    name,
    email,
    centers,
    lastname,
    role_alias,
    type = 'Director',
  } = director;
  const { t: translate } = useTranslation();
  const { navigateTo } = useRouter();
  const { can } = useCurrentUser();

  /* create list of assigned centers */
  const assignedCentersNames: any =
    centers?.map((item: any) => item.name) || [];

  const onGoToDirectorProfile = () => {
    navigateTo('/settings-staff', { userId: id, userType: type });
  };

  const buildList = (centers: any) => {
    return (
      <ul>
        {centers?.map((val: any) => (
          <li>{val}</li>
        ))}
      </ul>
    );
  };

  /* shorten teacher name to only display max. 70 characters */
  const truncateString = (str: string, length: number) => {
    if (str.length <= length) {
      return str;
    }
    return str.slice(0, length) + '...';
  };

  const canDelete = useMemo(
    () => can(ActionPermission.DELETE_DIRECTORS),
    // eslint-disable-next-line
    [director?.centers],
  );

  return (
    <DirectorContainer>
      <Name>
        <RendererIf condition={can(ActionPermission.MOVE_DIRECTORS_FROM_CENTER)} >
          <Checkbox
              label=" "
              name="permissions"
              checked={selected}
              onChange={() => toggleSelectDirector && toggleSelectDirector(director)}
            />
        </RendererIf>
        <Name onClick={onGoToDirectorProfile}>
          <CoverLetter>{name[0]}</CoverLetter>
          <p>{`${name} ${lastname}`}</p>
        </Name>
      </Name>
      <AssignedCenters onClick={onGoToDirectorProfile}>
        {assignedCentersNames.length > 0 ? (
          <Tooltip
            text={assignedCentersNames.join(', ')}
            contentPopUp={buildList(assignedCentersNames)}
          />
        ) : (
          'NA'
        )}
      </AssignedCenters>
      <Role onClick={onGoToDirectorProfile}>
        <Tooltip
          text={
            <span>{role_alias ? truncateString(role_alias, 10) : type}</span>
          }
          contentPopUp={role_alias || type}
        />
      </Role>
      <Email onClick={onGoToDirectorProfile}>
        <Tooltip
          text={<span>{truncateString(email, 20)}</span>}
          contentPopUp={email}
        />
      </Email>
      <StyledDropdown>
        <RenderIf condition={canDelete}>
          <Dropdown
            className="DropdownRemove"
            contentWidth={100}
            noPaddingContent={true}
            content={({ close }: any) => (
              <>
                <Overlay onClick={() => close()} />
                <DropdownContainer>
                  <span
                    onClick={() => {
                      onGoToDirectorProfile();
                      close();
                    }}
                  >
                    {translate('common:DETAILS')}
                  </span>
                  <span className="delete"
                    onClick={() => {
                      onClickDeleteDropDown && onClickDeleteDropDown(Number(id));
                      close();
                    }}
                  >
                    {translate('settings:DELETE')}
                  </span>
                </DropdownContainer>
              </>
            )}
            showIndicator={false}
            title={() => <Dots />}
          />
        </RenderIf>
      </StyledDropdown>
    </DirectorContainer>
  );
};

export default DirectorItem;
